import React, { useEffect, useState } from 'react';
import { withFormik, Field, Form, getIn } from 'formik';
import { Button, Row, Col } from 'react-bootstrap';
import { useDispatch } from 'react-redux';
import * as Yup from 'yup';
import { FormikInput, FormikRangePicker, FormikSelect } from '../../components';
import JobManagementsFilter from '../../components/Utils/Filters/JobManagementsFilter';
import JobTitlesFilter from '../../components/Utils/Filters/JobTitlesFilter';
import { BranchOfficesFilter } from '../../components/Utils/Filters';
import ShiftsFilter from '../../components/Utils/Filters/ShiftsFilter';
import { debounceIndexEmployeesRequest } from '../../requests/employees';

const ALL_SELECTED_LABEL = 'Todos';

const UnjustifiedAbsenceFilter = props => {
  const { errors, touched, setFieldValue, values } = props;
  const [employees, setEmployees] = useState([]);
  const [branchOffices, setBranchOffices] = useState([]);
  const dispatch = useDispatch();
  const [selectedShifts, setSelectedShifts] = useState([]);
  const [selectedJobTitles, setSelectedJobTitles] = useState([]);
  const [selectedJobManagements, setSelectedJobManagements] = useState([]);

  const fetchEmployees = () => {
    debounceIndexEmployeesRequest({
      dispatch,
      params: {
        active: true,
        sort_column: 'name',
        is_dt: false,
        display_length: 100,
        filter_active_branch_offices: branchOffices,
        filter_active_shifts: selectedShifts,
        filter_active_job_titles: selectedJobTitles,
        filter_active_job_managements: selectedJobManagements
      },
      successCallback: response => {
        const dataToGroupSearch = [{ id: 'Todos', label: 'Todos', value: 'Todos' }, ...response.data.data];
        setEmployees(response.data.data.length > 0 ? dataToGroupSearch : []);
      }
    });
  };

  const hasAllSelection = (groupSelection = []) => {
    return groupSelection.some(option => option.value === ALL_SELECTED_LABEL);
  };

  const handleDisabledSelection = (options, selectedOptions) => {
    if (hasAllSelection(selectedOptions)) {
      return true;
    }
    if (selectedOptions.length === 0) return false;
    return options.value === ALL_SELECTED_LABEL;
  };

  const handleEmployeesSearch = (field, data) => {
    switch (field) {
      case 'filter_active_branch_offices':
        setBranchOffices(data);
        break;
      case 'filterShift':
        setSelectedShifts(data);
        break;
      case 'filterJobTitle':
        setSelectedJobTitles(data);
        break;
      case 'filterJobManagement':
        setSelectedJobManagements(data);
        break;
      default:
        break;
    }
  };

  const handleNewMulti = (data, field, allowEmpty = false) => {
    const newData = data.map(element => element.value);
    if (newData.length && newData.includes(ALL_SELECTED_LABEL)) {
      newData.push('');
      return;
    }

    if (allowEmpty && !newData.length) newData.push('');
    setFieldValue(field, newData);
    handleEmployeesSearch(field, newData);
  };

  useEffect(() => {
    const jobTitles = values.filterJobTitle ? [values.filterJobTitle] : [];
    const jobManagements = values.filterJobManagement ? [values.filterJobManagement] : [];
    const branchOfficesFilter = values.filterBranchOffice ? [values.filterBranchOffice] : [];
    const shifts = values.filterShift ? [values.filterShift] : [];

    setSelectedShifts(shifts);
    setBranchOffices(branchOfficesFilter);
    setSelectedJobTitles(jobTitles);
    setSelectedJobManagements(jobManagements);
  }, [values.filterJobTitle, values.filterJobManagement, values.filterBranchOffice, values.filterShift]);

  useEffect(() => {
    fetchEmployees();
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    fetchEmployees();
    // eslint-disable-next-line
  }, [branchOffices, selectedShifts, selectedJobTitles, selectedJobManagements]);

  return (
    <Form className="absence-filter-form pt-2 px-3 mt-2">
      <Row>
        <Col xs={12} md={6}>
          <Field name="dateRange">
            {({ field }) => (
              <FormikRangePicker
                {...field}
                name={field.name}
                abbr
                startDateName="startDate"
                endDateName="endDate"
                startDate={values.startDate}
                endDate={values.endDate}
                showClearDates
                error={getIn(errors, field.name)}
                touched={getIn(touched, field.name)}
                appendToBody
                repositionPickerOnUpdate
              />
            )}
          </Field>
        </Col>
        <Col xs={12} md={3}>
          <Field name="filterNationalId">
            {({ field, form }) => (
              <FormikInput
                {...field}
                label="N° de Identificación"
                placeholder="Sin puntos y sin guion"
                onChange={e => form.setFieldValue(field.name, e.target.value)}
                error={getIn(form.touched, field.name) && getIn(form.errors, field.name)}
              />
            )}
          </Field>
        </Col>
        <Col xs={12} md={3}>
          <Field name="filterIds">
            {({ field }) => (
              <FormikSelect
                {...field}
                label="Nombres y Apellidos"
                isMulti
                options={employees}
                placeholder="Seleccione colaboradores"
                onChange={data => {
                  handleNewMulti(data || [], 'filter_ids', true);
                  setFieldValue(field.name, data || []);
                  setFieldValue('employee', '');
                  setFieldValue('filter_id', '');
                }}
                isOptionDisabled={handleDisabledSelection}
                isClearable
                error={getIn(errors, field.name)}
                touched={getIn(touched, field.name)}
              />
            )}
          </Field>
        </Col>
      </Row>
      <Row className="mt-3">
        <Col xs={12} md={3}>
          <JobManagementsFilter formikValue="filterJobManagement" params={{}} />
        </Col>
        <Col xs={12} md={3}>
          <JobTitlesFilter formikValue="filterJobTitle" params={{}} />
        </Col>
        <Col xs={12} md={3}>
          <BranchOfficesFilter formikValue="filterBranchOffice" params={{}} />
        </Col>
        <Col xs={12} md={3}>
          <ShiftsFilter formikValue="filterShift" params={{}} />
        </Col>
      </Row>
      <Row className="my-3">
        <Col xs={12} md={{ span: 2, offset: 10 }} className="d-flex align-items-end justify-content-end">
          <Button type="submit" className="btn-block btn-primary mb-3">
            FILTRAR
          </Button>
        </Col>
      </Row>
    </Form>
  );
};

const validationSchema = Yup.object().shape({
  dateRange: Yup.array()
    .required('Debes seleccionar un rango de fechas')
    .rangedate(true)
});

const setInitialValues = () => {
  return {
    dateRange: [],
    filterNationalId: '',
    filterIds: '',
    filterJobManagement: '',
    filterJobTitle: '',
    filterShift: '',
    filterBranchOffice: '',
    filterActiveBranchOffices: []
  };
};

const handleSubmit = (values, { props }) => {
  const { formRequest } = props;
  formRequest(values);
};

export default withFormik({
  mapPropsToValues: setInitialValues,
  validationSchema,
  handleSubmit,
  enableReinitialize: true
})(UnjustifiedAbsenceFilter);
